// Table view: rows of topic, mentions, velocity, sparkline. Lifted from
// view-terminal.jsx, simplified for v1 (no separate authors/eng columns
// since those metrics don't exist on the counts endpoint).

import { COLORS, fmtRaw, tStyles } from "../styles";

export interface TableRow {
  name: string;
  raw: number;
  velocity: number | null;
  spark: number[];
  watched?: boolean;
}

interface Props {
  rows: TableRow[];
  cursorIdx: number;
  selectedName: string | null;
  onSelect: (idx: number) => void;
}

export function TopicTable({ rows, cursorIdx, selectedName, onSelect }: Props) {
  return (
    <div style={tStyles.tableBox}>
      <div style={tStyles.tableHead}>
        <span>#</span>
        <span>topic</span>
        <span style={tStyles.thNum}>mentions</span>
        <span style={tStyles.thNum}>vel</span>
        <span style={tStyles.thNum}>cov</span>
        <span style={tStyles.thSpark}>24h trend</span>
      </div>
      <div style={tStyles.tableBody}>
        {rows.map((r, i) => {
          const isCursor = i === cursorIdx;
          const isSel = selectedName === r.name;
          const rowStyle = {
            ...tStyles.tr,
            ...(isCursor ? tStyles.trCursor : {}),
            ...(isSel ? tStyles.trSelected : {}),
          };
          return (
            <div key={r.name} onClick={() => onSelect(i)} style={rowStyle}>
              <span style={tStyles.tdRank}>{String(i + 1).padStart(2, "0")}</span>
              <span style={tStyles.tdName}>
                {r.watched && (
                  <span style={{ color: COLORS.accent, marginRight: 6 }}>★</span>
                )}
                {r.name}
              </span>
              <span style={tStyles.tdNum}>{fmtRaw(r.raw)}</span>
              <span
                style={{
                  ...tStyles.tdNum,
                  color:
                    r.velocity === null
                      ? COLORS.textDim
                      : r.velocity >= 0
                        ? COLORS.up
                        : COLORS.down,
                }}
              >
                {r.velocity === null
                  ? "—"
                  : `${r.velocity >= 0 ? "+" : ""}${r.velocity.toFixed(1)}%`}
              </span>
              <span style={{ ...tStyles.tdNum, color: COLORS.textDim }}>
                {Math.round(coverage(r.spark) * 100)}%
              </span>
              <span style={tStyles.tdSpark}>
                <svg viewBox="0 0 100 20" preserveAspectRatio="none" width="100%" height="20">
                  <polyline
                    points={r.spark
                      .map(
                        (v, j) =>
                          `${(j / Math.max(1, r.spark.length - 1)) * 100},${20 - v * 16 - 2}`,
                      )
                      .join(" ")}
                    fill="none"
                    stroke={
                      r.velocity === null
                        ? COLORS.textDim
                        : r.velocity >= 0
                          ? COLORS.up
                          : COLORS.down
                    }
                    strokeWidth="1"
                  />
                </svg>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function coverage(spark: number[]): number {
  if (spark.length === 0) return 0;
  const nonzero = spark.filter((v) => v > 0).length;
  return nonzero / spark.length;
}
